import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider, type AuthProviderProps } from 'react-oidc-context';
import { MainErrorFallback } from '@/app/components/errors/main-error.tsx';
import {
  apiConfig,
  type apiConfigType,
  type authProps
} from '@/api/configs/apiConfig.ts';
import { QueryProvider } from '@/app/QueryProvider.tsx';
import { config } from '@/api/configs/config.ts';
import { WebStorageStateStore } from 'oidc-client-ts';
import { GroupuiLoadingSpinner } from '@group-ui/group-ui-react';

interface AppProviderProps {
  children: ReactNode;
}

const onSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const { VITE_ENV } = config;

const oidcConfig: AuthProviderProps = {
  authority: (apiConfig[VITE_ENV as keyof apiConfigType] as authProps)?.authURL,
  client_id: (apiConfig[VITE_ENV as keyof apiConfigType] as authProps)
    ?.clientId,
  scope: 'profile openid',
  redirect_uri: (apiConfig[VITE_ENV as keyof apiConfigType] as authProps)
    ?.redirect,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback
};

export function AppProvider({
  children
}: Readonly<AppProviderProps>): ReactNode {
  return (
    <Suspense
      fallback={
        <div className='flex h-screen w-screen items-center justify-center'>
          <GroupuiLoadingSpinner displayed />
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={MainErrorFallback}>
        <AuthProvider {...oidcConfig}>
          <QueryProvider>{children}</QueryProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Suspense>
  );
}
